import React, { useEffect } from 'react'
import ReactMarkdown from 'react-markdown'
import dynamic from 'next/dynamic'
import rehypeExternalLinks from 'rehype-external-links'
import rehypeRaw from 'rehype-raw'
import rehypeSlug from 'rehype-slug'
import remarkBreaks from 'remark-breaks'
import remarkDirective from 'remark-directive'
import remarkFrontmatter from 'remark-frontmatter'
import remarkGfm from 'remark-gfm'
import remarkHtml from 'remark-html'
import remarkMermaid from 'remark-mermaidjs'
import remarkParse from 'remark-parse'
import { addAriaLabelToSvg } from '@/utils/dom-elements'
import { USBIconInfo, USBIconWarningSign, USBIconStarSolid ,USBIconPhone,USBIconEnvelope} from '@usb-shield/react-icons'
import USBLink from '@usb-shield/react-link'
import parse from 'html-react-parser'
import sanitizeHtml from 'sanitize-html'
import shortcodePlugin from './ShortcodePlugin'
import Heading from '@/components/Headings/Headings'
import CodeBlocks from '@/components/ReactMarkDown/CodeBlocks'
import { useWindowFocus } from 'src/common/hooks/useWindowFocus/useWindowFocus'
import remarkUnwrapImages from 'remark-unwrap-images'
import { handleAnchorClick } from '@/components/ReactMarkDown/utils/callbacks'

const imgLinks = require("@pondorasti/remark-img-links");

const SyntaxHighlighter: any = dynamic(() => import('react-syntax-highlighter'), {
  ssr: false,
})

const ReactMarkDown = ({ content, cb, router }: { content: any, cb?: () => void, router?: any }) => {
  // temporary fix till AEM addresses these characters
  const data = content
    ?.replaceAll('&lt;', '<')
    ?.replaceAll('&gt;', '>')
    ?.replaceAll('&nbsp;', ' ')
  
  const focused = useWindowFocus()

  useEffect(() => {
    const tableEl: any = document.querySelectorAll('.markdown-block table')

    for (const tableElement of tableEl) {
      tableElement.setAttribute('class', 'markdownTable')
      tableElement.setAttribute('tabindex', '0')
      const thEls = tableElement.querySelectorAll('thead th')
      const tdLabels = Array.from(thEls).map((el: any) => el.innerText)
      tableElement.querySelectorAll('tbody tr').forEach((tr: any) => {
        Array.from(tr.children).forEach((td: any, ndx: any) => {
          td.setAttribute('label', tdLabels[ndx])
        })
      })
    }
    // const baseURI = document.baseURI.split('#')[1]
    
    // if (baseURI && baseURI.length) {
    //   scrollToElement(baseURI)
    // }

    // ACCESSIBILITY VIOLATION FIXES
    addAriaLabelToSvg()

    if (cb) {
      cb()
    } 
  }, [focused])

  const generateAnchor = (props: any) => {
    return (
      <>
        <a id={props.id} className="anchor" aria-label=""></a>
        <Heading level={props.level}>{props.children}</Heading>
      </>
    )
  }

  const isHTML = RegExp.prototype.test.bind(/(<([^>]+)>)/i);

  const getImage = ({ node, className, children, ...props }: any) => {
    let src = process.env.CONTENT_DOMAIN + node.properties.src
    return <img alt="" src={src} {...props} />
  }

  const getLink = ({ node, className, children, ...props }: any) => {
    if (children === undefined) {
      return <></>
    }
    const text = children[0]
    const href = node.properties.href
    let hrefFlag: boolean
    if (href.includes("#") && !href.split('#')[0]) {
      hrefFlag = true
    } else {
      hrefFlag = false
    }
    return node.properties.title === 'download' ? (
      
      <USBLink href={href} {...props} download>
        {text}
      </USBLink>
    ) : (
      <USBLink 
        href={href} 
        {...props} 
        linkType="inline" 
        {...(hrefFlag && router && { handleClick: (e: any) => handleAnchorClick(e, router) })}
      >
        {text}
      </USBLink>
    )
  }

  const getParagraph = ({ node, className, children, ...props }: any) => {
    const imgTag = node?.children?.find(
      (res: any) => res?.tagName === 'img'
    )
    if (imgTag) {
      let src = process.env.CONTENT_DOMAIN + imgTag?.properties?.src
      let altText = imgTag?.properties?.alt ? imgTag?.properties?.alt : ''
      return <img alt={altText} src={src} {...props} />
    }
    if (isHTML(children)) {
      const parserData = sanitizeHtml(children[0], {
        allowedTags: sanitizeHtml.defaults.allowedTags.concat(["a"]),
      });
        return <p>{parse(parserData)}</p>     // to parse the html in string
      } else {
        return <p>{children}</p>
      } 
  }

  const getListItem = ({ node, className, id, children, ...props }: any) => {
    const imgTag = node?.children?.find(
      (res: any) => res?.tagName === 'img'
    )
    if (imgTag) {
      let src = process.env.CONTENT_DOMAIN + imgTag?.properties?.src;
      let altText = imgTag?.properties?.alt
        ? imgTag?.properties?.alt
        : "";
      return (
        <>
          <li>
            <p>{children.slice(0, -1)}</p>
            <p>
              <img alt={altText} src={src} {...props} />
            </p>
          </li>
        </>
      )
    }
    return id && id.length ? <li id={id}>{children}</li> : <li>{children}</li>;
  }

  const getVarCodeBlock = ({ className, children }: any) => {
    return (className === 'code-example')
      ? <CodeBlocks codeBlocks={children} />
      : null;
  }

  const getCode = ({ inline, className, children, ...props } : any) => {
    const match = /language-(\w+)/.exec(className ?? '')
    return (!inline && match) ? (
      <SyntaxHighlighter
        language={match[1]}
        useInlineStyles={false}
        className="code-example"
        tabIndex={0}
      >
        {children}
      </SyntaxHighlighter>
    ) : (
      <code className={className} {...props}>
        {children}
      </code>
    )
  }

  const getSvg = ({ node, className, children, ...props }: any) => {
    if(className === 'notice-icon'){
      switch(props.variant) {
        case 'info':
        case 'tip':
        case 'note':
          return (
            <USBIconInfo 
              colorVariant="neutral" 
              addClasses={className}
              size={18}
            />
          )
        case 'important':
          return (
            <USBIconInfo 
              colorVariant="grey80" 
              addClasses={className}
              size={18}
            />
          )
        case 'warning':
          return (
            <USBIconWarningSign 
              colorVariant="error" 
              addClasses={className}
              size={18}
            />
          )
        default:
          return <USBIconStarSolid />
      }
    }
    else if(className === 'phone-card-icon'){
      return (
        <USBIconPhone 
          colorVariant="interaction" 
          addClasses={className}
          size={28}
        />
      )
    }
    else if(className === 'email-card-icon'){
      return (
        <USBIconEnvelope 
          colorVariant="interaction" 
          addClasses={className}
          size={28}
        />
      )
    }
    else {
      return (
        <svg {...props}>
          {children}
        </svg>
      )
    }
  }

  return (
    <ReactMarkdown
      remarkPlugins={[
        remarkGfm,
        [remarkFrontmatter, ['yaml', 'toml']],
        remarkHtml,
        remarkParse,
        remarkBreaks,
        [remarkMermaid],
        remarkDirective,
        shortcodePlugin,
        remarkUnwrapImages,
        [imgLinks, { absolutePath: process.env.CONTENT_DOMAIN }],
      ]}
      rehypePlugins={[
        rehypeSlug,
        rehypeExternalLinks,
        rehypeRaw
      ]}
      remarkRehypeOptions={{allowDangerousHtml: true}}
      components={{
        h2: ({...props}) => {
          return generateAnchor(props)
        },
        h3: ({...props}) => {
          return generateAnchor(props)
        },
        h4: ({...props}) => {
          return generateAnchor(props)
        },
        h5: ({...props}) => {
          return generateAnchor(props)
        },
        h6: ({...props}) => {
          return generateAnchor(props)
        },
        image: ({...props}) => {
          return getImage(props)
        },
        a: ({...props}) => {
          return getLink(props)
        },
        p: ({...props}) => {
          return getParagraph(props)
        },
        li: ({...props}) => {
          return getListItem(props)
        },
        'var': ({...props}) => {
          return getVarCodeBlock(props)
        },
        code: ({...props}) => {
          return getCode(props)
        },
        svg: ({...props}) => {
          return getSvg(props)
        },
      }}
    >
      {data}
    </ReactMarkdown>
  )
}

export default ReactMarkDown